import { IPagination } from "../../../shared/interfaces/shared/IPagination";
import { requests, formDataHeaders, instance } from "../../../utils/api/api.service";
import { WEB_PROTOCOLS_URL } from "../../../utils/api/endpoints";
import { IProtocol } from "../../../shared/interfaces/protocol/IProtocol";
import { IProtocolItem, IProtocolItemCreate } from "../../../shared/interfaces/protocol/IProtocolItem";
import { IBaseModel } from "../../../shared/interfaces/shared/IBase";

export const WebProtocolService = {
  getAll: (params: IPagination): Promise<IBaseModel<IProtocol[]>> =>
    requests.get(WEB_PROTOCOLS_URL, params),
  getSingle: (id: number) => requests.get(`${WEB_PROTOCOLS_URL}/${id}`),
  create: (payload: IProtocol): Promise<IProtocol> =>
    requests.post(WEB_PROTOCOLS_URL, payload),
  update: (payload: IProtocol, id?: number): Promise<IProtocol> =>
    requests.put(`${WEB_PROTOCOLS_URL}/${id}`, payload),
  delete: (id: number) => requests.delete(`${WEB_PROTOCOLS_URL}/${id}`),
  
  // Protocol Items
  getItems: (protocolId: number): Promise<IBaseModel<IProtocolItem[]>> =>
    requests.get(`${WEB_PROTOCOLS_URL}/${protocolId}/items`),
  getItem: (itemId: number): Promise<{ data: IProtocolItem }> =>
    requests.get(`${WEB_PROTOCOLS_URL}/items/${itemId}`),
  createItem: (protocolId: number, payload: IProtocolItemCreate, files?: { [lang: string]: File[] }): Promise<IProtocolItem> => {
    const formData = new FormData();
    formData.append('text', payload.text);
    formData.append('mediaType', payload.mediaType);
    formData.append('protocolId', String(protocolId));
    
    if (payload.contents) {
      Object.entries(payload.contents).forEach(([lang, content]) => {
        if (content.text) {
          formData.append(`contents[${lang}].text`, content.text);
        }
      });
    }
    
    if (files) {
      Object.entries(files).forEach(([lang, fileList]) => {
        if (fileList && fileList.length > 0) {
          fileList.forEach((file) => {
            formData.append(`contents[${lang}].files`, file);
          });
        }
      });
    }
    
    return instance.post(`${WEB_PROTOCOLS_URL}/${protocolId}/items`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data.data || res.data);
  },
  updateItem: (itemId: number, payload: IProtocolItemCreate, files?: { [lang: string]: File[] }): Promise<IProtocolItem> => {
    const formData = new FormData();
    formData.append('text', payload.text);
    formData.append('mediaType', payload.mediaType);
    if (payload.protocolId) {
      formData.append('protocolId', String(payload.protocolId));
    }
    
    if (payload.contents) {
      Object.entries(payload.contents).forEach(([lang, content]) => {
        if (content.text) {
          formData.append(`contents[${lang}].text`, content.text);
        }
      });
    }
    
    if (files) {
      Object.entries(files).forEach(([lang, fileList]) => {
        if (fileList && fileList.length > 0) {
          fileList.forEach((file) => {
            formData.append(`contents[${lang}].files`, file);
          });
        }
      });
    }
    
    return instance.put(`${WEB_PROTOCOLS_URL}/items/${itemId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data.data || res.data);
  },
  deleteItem: (itemId: number): Promise<void> =>
    requests.delete(`${WEB_PROTOCOLS_URL}/items/${itemId}`),
  sortItems: (protocolId: number, items: Array<{ id: number; sortOrder: number }>): Promise<void> =>
    requests.put(`${WEB_PROTOCOLS_URL}/${protocolId}/items/sort`, { items }),
  
  // Protocol Item Content
  uploadItemContent: (protocolId: number, itemId: number, lang: string, files: File[]): Promise<void> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append(`contents[${lang}].files`, file);
    });
    return requests.post(`${WEB_PROTOCOLS_URL}/${protocolId}/items/${itemId}/content`, formData, formDataHeaders);
  },
  getItemContent: async (itemId: number): Promise<string | null> => {
    try {
      const response = await instance.get(`${WEB_PROTOCOLS_URL}/items/${itemId}/content`, {
        responseType: 'blob',
      });
      if (response.data && response.data.size > 0) {
        return URL.createObjectURL(response.data);
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch item content:', error);
      return null;
    }
  },
};
